import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { QUERY_CACHE_CONFIG, queryKeys } from '../config/queryConfig';
import {
  Building2,
  Factory,
  Building,
  Users,
  UserCheck,
  MapPin,
  Camera,
  Activity,
  AlertTriangle,
  Eye,
  CheckCircle,
  Clock,
  Zap,
  Filter
} from 'lucide-react';
import { condoApi, fetchWithAuth } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { cn } from '../utils/cn';
import { useCustomerFilter } from '../contexts/CustomerFilterContext';
import { useCustomer } from '../contexts/CustomerContext';
import { CustomerInfoHeader } from '../components/dashboard/CustomerInfoHeader';
import { Customer } from '../types/customer';
import { usePersonDetail } from '../hooks/usePersonDetail';
import { getAbsoluteImageUrl } from '../utils/imageUrl';
import { isReplayedEvent, getDisplayTimestamp, formatRelativeTime } from '../utils/eventUtils';
import { LicensePlate } from '../components/ui/LicensePlate';

const Dashboard: React.FC = () => {
  console.log(' Dashboard component rendering');

  const navigate = useNavigate();
  const { selectedCustomers, isFilterActive } = useCustomerFilter();
  const { selectedCustomer, availableCustomers, isLoading: customersLoading } = useCustomer();
  const { openPersonDetail } = usePersonDetail();

  const handleVehicleClick = async (log: any, plate: string) => {
    if (!plate || !selectedCustomer) return;
    let unitId = log.unit_id;
    if (!unitId) {
      try {
        const url = log.vehicle_id
          ? `/api/v1/customers/${selectedCustomer.id}/vehicles/${log.vehicle_id}`
          : `/api/v1/customers/${selectedCustomer.id}/vehicles?search=${encodeURIComponent(plate)}`;
        const res = await fetchWithAuth(url);
        if (res.ok) {
          const data = await res.json();
          const v = log.vehicle_id ? data.data : (Array.isArray(data) ? data[0] : data.vehicles?.[0]);
          unitId = v?.unit_id;
        }
      } catch { /* ignore */ }
    }
    if (unitId) navigate(`/units?unit=${unitId}`);
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: queryKeys.dashboard.stats(selectedCustomer?.id || ''),
    queryFn: () => condoApi.getDashboardStats(selectedCustomer?.id),
    enabled: !!selectedCustomer?.id,
    ...QUERY_CACHE_CONFIG.dashboard,  // 15 min cache for dashboard stats
  });

  // Use customers from CustomerContext instead of separate API call
  // This eliminates duplicate requests since CustomerContext already loads customers
  const customers: Customer[] = availableCustomers;

  const { data: accessLogs, isLoading: logsLoading } = useQuery({
    queryKey: ['recent-access-logs', selectedCustomer?.id],
    queryFn: async () => {
      // Use /events/access instead of /access-logs — it returns vehicle_plate and richer fields
      const params = new URLSearchParams({
        customer_id: selectedCustomer!.id,
        limit: '10',
        sort_by: 'processed_at',
      });
      const res = await fetchWithAuth(`/api/v1/events/access?${params}`);
      if (!res.ok) return { data: [] };
      return res.json();
    },
    enabled: !!selectedCustomer?.id,
    ...QUERY_CACHE_CONFIG.dashboard,
  });

  // Filter customers based on selected customers
  const filteredCustomers = isFilterActive
    ? customers.filter((customer: Customer) => selectedCustomers.includes(customer.id))
    : customers;

  // Context-aware labels
  const isEmpresa = selectedCustomer?.type === 'empresa';
  const peopleLabel = isEmpresa ? 'Pessoas Ativas' : 'Moradores Ativos';
  const peopleDescription = isEmpresa
    ? 'Total de pessoas registradas com acesso'
    : 'Total de moradores com acesso autorizado';

  // Get the actual stats object
  const dashboardStats = stats?.data;

  // Debug logging to see actual stats data (check both camelCase and snake_case)
  console.log(' Dashboard Stats Debug:', {
    fullStats: stats,
    statsData: stats?.data,
    dashboardStats,
    // Check camelCase fields
    totalPeople: dashboardStats?.totalPeople,
    totalResidents: dashboardStats?.totalResidents,
    totalStaff: dashboardStats?.totalStaff,
    totalVisitors: dashboardStats?.totalVisitors,
    todayAccess: dashboardStats?.todayAccess,
    totalDevices: dashboardStats?.totalDevices,
    activeDevices: dashboardStats?.activeDevices,
    // Check snake_case fields (backend sends these)
    total_people: dashboardStats?.total_people,
    total_residents: dashboardStats?.total_residents,
    total_staff: dashboardStats?.total_staff,
    total_visitors: dashboardStats?.total_visitors,
    today_access: dashboardStats?.today_access,
    total_devices: dashboardStats?.total_devices,
    active_devices: dashboardStats?.active_devices,
    // Check devices array
    devices: stats?.data?.devices,
    devicesLength: stats?.data?.devices?.length,
    // Context
    customerType: selectedCustomer?.type,
    isEmpresa
  });

  const accessStats = [
    {
      title: peopleLabel,
      value: isEmpresa
        ? (dashboardStats?.totalStaff || dashboardStats?.total_staff || 0)  // Support both camelCase and snake_case
        : (dashboardStats?.totalResidents || dashboardStats?.total_residents || 0),
      icon: Users,
      description: peopleDescription
    },
    {
      title: 'Visitantes Ativos',
      value: dashboardStats?.totalVisitors || dashboardStats?.total_visitors || 0,  // Support both formats
      icon: UserCheck,
      description: 'Visitantes com acesso autorizado'
    },
    {
      title: 'Acessos Hoje',
      value: dashboardStats?.todayAccess || dashboardStats?.today_access || 0,  // Support both formats
      icon: Activity,
      description: 'Eventos de acesso hoje'
    },
  ];

  const customerStats = [
    {
      name: 'Condomínios',
      value: filteredCustomers?.filter((c: Customer) => c.type === 'condominio').length || 0,
      icon: Building2,
      color: 'bg-blue-600'
    },
    {
      name: 'Empresas',
      value: filteredCustomers?.filter((c: Customer) => c.type === 'empresa').length || 0,
      icon: Building,
      color: 'bg-green-600'
    }
  ];

  // Skeleton components for progressive loading
  const StatCardSkeleton = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 animate-pulse">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="w-12 h-12 bg-gray-200 rounded-xl mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
          <div className="h-8 bg-gray-200 rounded w-16 mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-32"></div>
        </div>
      </div>
    </div>
  );

  const CustomerStatSkeleton = () => (
    <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl animate-pulse">
      <div className="w-12 h-12 bg-gray-200 rounded-xl"></div>
      <div>
        <div className="h-6 bg-gray-200 rounded w-12 mb-1"></div>
        <div className="h-4 bg-gray-200 rounded w-20"></div>
      </div>
    </div>
  );

  const ActivityItemSkeleton = () => (
    <div className="flex items-center space-x-2.5 p-3 bg-gray-50 rounded-lg animate-pulse">
      <div className="w-9 h-9 bg-gray-200 rounded-full"></div>
      <div className="flex-1">
        <div className="h-3 bg-gray-200 rounded w-32 mb-1"></div>
        <div className="h-2.5 bg-gray-200 rounded w-24"></div>
      </div>
      <div className="text-right">
        <div className="h-2.5 bg-gray-200 rounded w-10 mb-1"></div>
        <div className="h-4 bg-gray-200 rounded w-14"></div>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between">
        <div>
          {isFilterActive && (
            <div className="flex items-center space-x-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg max-w-fit">
              <Filter className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-700">
                Filtrando por {selectedCustomers.length} cliente{selectedCustomers.length > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3 mt-4 lg:mt-0">
          <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-full">
            <Clock className="h-4 w-4 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Ao Vivo</span>
          </div>
        </div>
      </div>

      {/* Customer Information Header */}
      {selectedCustomer && <CustomerInfoHeader customer={selectedCustomer} />}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          accessStats.map((stat, index) => {
            const IconComponent = stat.icon;
            const gradients = [
              'from-gray-800 to-black',
              'from-green-500 to-green-600',
              'from-orange-500 to-orange-600'
            ];
            return (
              <div key={stat.title} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className={`inline-flex p-3 rounded-xl bg-gradient-to-r ${gradients[index]} mb-4`}>
                      <IconComponent className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-2">{stat.value.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{stat.description}</p>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Customer Overview */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <Building2 className="h-5 w-5 text-gray-700" />
          <h2 className="text-lg font-semibold text-gray-900">Clientes Monitorados</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {customersLoading ? (
            <>
              <CustomerStatSkeleton />
              <CustomerStatSkeleton />
            </>
          ) : (
            customerStats.map((customerStat, index) => {
              const IconComponent = customerStat.icon;
              return (
                <div key={customerStat.name} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                  <div className={cn('p-3 rounded-xl', customerStat.color)}>
                    <IconComponent className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{customerStat.value}</p>
                    <p className="text-sm font-medium text-gray-600">{customerStat.name}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Real-time Access Activity */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Activity className="h-5 w-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">Atividade em Tempo Real</h2>
            </div>
            <div className="flex items-center space-x-2 px-3 py-1 bg-green-50 border border-green-200 rounded-full">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-green-700">Ao Vivo</span>
            </div>
          </div>
          <div className="space-y-2">
            {logsLoading ? (
              <>
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
                <ActivityItemSkeleton />
              </>
            ) : accessLogs?.data && accessLogs.data.length > 0 ? (
              accessLogs.data
                .filter((log: any) => !isReplayedEvent(log))  // Hide replayed "Buffer" events from dashboard
                .slice(0, 6)
                .map((log: any, idx: number) => {
                // Determine access status from event_type or is_authorized
                const isGranted = log.event_type?.includes('granted') || log.is_authorized === true;
                const isDenied = log.event_type?.includes('denied') || log.is_authorized === false;
                const isRemoteDoor = log.event_type === 'remote_door_trigger';

                // Format direction label
                const directionLabel = log.direction === 'entrada' ? 'Entrada' :
                                     log.direction === 'saida' ? 'Saída' :
                                     log.direction === 'passagem' ? 'Passagem' : '';

                const hasPersonId = log.person_id && log.person_id !== '00000000-0000-0000-0000-000000000000';
                const isVehicleEvent = !!log.vehicle_plate;
                const vehiclePlate: string | null = log.vehicle_plate || null;
                const isClickable = hasPersonId || isVehicleEvent;
                // Owner name: present when vehicle has a registered owner (person_name is not the plate text)
                const ownerName = isVehicleEvent && log.person_name ? log.person_name : null;

                return (
                  <div
                    key={log.id}
                    className={cn(
                      "flex items-center space-x-2.5 p-3 bg-gray-50 rounded-lg transition-colors",
                      isClickable ? "hover:bg-gray-100 cursor-pointer" : ""
                    )}
                    onClick={() => {
                      if (hasPersonId) openPersonDetail(log.person_id);
                      else if (isVehicleEvent && vehiclePlate) handleVehicleClick(log, vehiclePlate);
                    }}
                  >
                    {/* Avatar: license plate for vehicle events, photo/initials for persons */}
                    {isVehicleEvent && vehiclePlate ? (
                      <LicensePlate plate={vehiclePlate} size="sm" className="flex-shrink-0" />
                    ) : (
                      <div className="flex-shrink-0">
                        {log.identity_photo_url || log.face_photo_url ? (
                          <img
                            src={getAbsoluteImageUrl(log.identity_photo_url || log.face_photo_url)}
                            alt={log.person_name || 'Person'}
                            className="w-7 h-9 rounded-md object-cover border-2 border-gray-200"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              target.nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        <div className={cn(
                          'w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium',
                          log.identity_photo_url || log.face_photo_url ? 'hidden' : '',
                          isGranted && 'bg-green-100 text-green-700',
                          isDenied && 'bg-red-100 text-red-700',
                          isRemoteDoor && 'bg-blue-100 text-blue-700',
                          !isGranted && !isDenied && !isRemoteDoor && 'bg-gray-200 text-gray-600'
                        )}>
                          {log.person_name ? log.person_name.charAt(0).toUpperCase() : '?'}
                        </div>
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      {isVehicleEvent ? (
                        <>
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {ownerName
                              ? `${ownerName}${directionLabel ? ` • ${directionLabel}` : ''}`
                              : (directionLabel || 'Veículo')}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {log.unit_name || log.department_name || log.zone_name || log.device_name || 'Sem localização'}
                          </p>
                        </>
                      ) : (
                        <>
                          <p className={cn(
                            "text-xs font-medium text-gray-900 truncate",
                            hasPersonId && "hover:text-primary-600"
                          )}>
                            {log.person_name || 'Desconhecido'}
                            {directionLabel && ` • ${directionLabel}`}
                          </p>
                          <p className="text-[11px] text-gray-500 truncate">
                            {log.zone_name || log.device_name || 'Sem localização'}
                            {log.department_name && ` • ${log.department_name}`}
                            {log.unit_name && ` • ${log.unit_name}`}
                            {isRemoteDoor && (log.triggered_by_email || log.raw_event_data?.triggered_by?.email) && ` • ${log.triggered_by_email || log.raw_event_data?.triggered_by?.email}`}
                          </p>
                        </>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center gap-1 justify-end mb-0.5">
                        <p className="text-[11px] text-gray-500">
                          {log.processed_at ? formatRelativeTime(getDisplayTimestamp(log, 'realtime')) : '--:--'}
                        </p>
                        {log.processed_at && isReplayedEvent(log) && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-yellow-100 text-yellow-800 rounded">
                            Buffer
                          </span>
                        )}
                      </div>
                      <span className={cn(
                        'inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded-full',
                        isGranted && 'bg-green-100 text-green-800',
                        isDenied && 'bg-red-100 text-red-800',
                        isRemoteDoor && 'bg-blue-100 text-blue-800',
                        !isGranted && !isDenied && !isRemoteDoor && 'bg-gray-100 text-gray-800'
                      )}>
                        {isGranted ? 'Permitido' : isDenied ? 'Negado' : isRemoteDoor ? 'Remoto' : 'Evento'}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-12">
                <Activity className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-500">Nenhuma atividade recente</p>
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center space-x-2 mb-6">
            <Zap className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-semibold text-gray-900">Ações Rápidas</h2>
          </div>
          <div className="space-y-4">
            <button
              onClick={() => navigate('/people')}
              className="w-full flex items-center justify-start p-4 bg-black text-white rounded-xl hover:bg-gray-800 transition-colors"
            >
              <Users className="h-5 w-5 mr-3" />
              <span className="font-medium">Gerenciar Pessoas</span>
            </button>
            <button
              onClick={() => navigate('/zones')}
              className="w-full flex items-center justify-start p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <MapPin className="h-5 w-5 mr-3 text-gray-600" />
              <span className="font-medium text-gray-900">Configurar Zonas</span>
            </button>
            <button
              onClick={() => navigate('/facial')}
              className="w-full flex items-center justify-start p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Camera className="h-5 w-5 mr-3 text-gray-600" />
              <span className="font-medium text-gray-900">Gerenciar Facial</span>
            </button>
            <button
              onClick={() => navigate('/access-logs')}
              className="w-full flex items-center justify-start p-4 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Eye className="h-5 w-5 mr-3 text-gray-600" />
              <span className="font-medium text-gray-900">Ver Logs de Acesso</span>
            </button>
            <button
              onClick={() => navigate('/settings')}
              className="w-full flex items-center justify-start p-4 text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
            >
              <AlertTriangle className="h-5 w-5 mr-3" />
              <span className="font-medium">Configurações do Sistema</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
